import { getRequest } from "api/commonActions/get";
import { postRequest } from "api/commonActions/post";
import { patchRequest } from "api/commonActions/update";
import { Requests } from "config/api";
import { CarType } from "types/loadedData";

export type DistributionPlatform = "Gari" | "Jiji" | "Facebook";
export type DistributionStatus =
  | "Ready"
  | "Processing"
  | "NeedsReview"
  | "Opened"
  | "Published"
  | "Unavailable"
  | "Failed";

export type ListingDistribution = {
  _id: string;
  car: string;
  platform: DistributionPlatform;
  status: DistributionStatus;
  title: string;
  description: string;
  phone: string;
  images: string[];
  targetUrl: string;
  publishedUrl?: string;
  externalPostId?: string;
  lastError?: string;
  progressMessage?: string;
  snapshot?: {
    make?: string;
    model?: string;
    year?: number;
    price?: number;
    usageType?: string;
    listingType?: string;
    fuelType?: string;
    transmissionType?: string;
    mileage?: number;
    color?: string;
    location?: string;
    bodyType?: string;
    inspectionStatus?: string;
    sourceProvider?: string;
  };
  updatedAt: string;
  publishedAt?: string;
};

export type DistributionCar = CarType & {
  distributions: ListingDistribution[];
  blockedPlatforms?: Partial<Record<DistributionPlatform, string>>;
};

const messageOf = (response: any, fallback: string) =>
  response?.data?.msg || response?.data?.error || response?.data?.details || fallback;

export const GetDistributionCars = async (query: {
  search?: string;
  limit?: number;
  offset?: number;
}) => {
  const response = await getRequest({
    route: Requests.get.getDistributionCars,
    queryParams: query,
  });
  return {
    status: response?.status,
    message: messageOf(response, "Could not load distribution cars."),
    cars: (response?.data?.cars || []) as DistributionCar[],
    count: Number(response?.data?.count || 0),
  };
};

export const PrepareDistribution = async (
  carId: string,
  platform: DistributionPlatform
) => {
  const response = await postRequest({
    route: Requests.post.prepareDistribution,
    payload: { carId, platform },
  });
  return {
    status: response?.status,
    message: messageOf(response, "Could not prepare this listing."),
    distribution: response?.data?.distribution as ListingDistribution,
  };
};

export const UpdateDistribution = async (
  id: string,
  payload: {
    status: Exclude<DistributionStatus, "Ready">;
    publishedUrl?: string;
    externalPostId?: string;
    lastError?: string;
  }
) => {
  const response = await patchRequest({
    route: Requests.update.updateDistribution,
    pathVariables: { id },
    payload,
  });
  return {
    status: response?.status,
    message: messageOf(response, "Could not update publication status."),
    distribution: response?.data?.distribution as ListingDistribution,
  };
};
